import {put, call, all} from 'redux-saga/effects';
import Calendar from "../../entities/calendar";
import * as Actions from "../actions/calendarActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CalendarAction = {
    calendarId: string;
    calendar: Calendar;
    calendars: Calendar[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCalendar(action: CalendarAction) {
	const session = tabSession.captureGeneration();
	try {
		const calendar: Calendar = yield call(api.findOne, 'calendar', action.calendarId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CALENDAR_FETCH_ONE_SUCCESS, calendarId: action.calendarId, calendar, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDAR_FETCH_ONE_FAILURE, calendarId: action.calendarId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CalendarAction} action
 */
export function* saveCalendar(action: CalendarAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CALENDAR_SAVE saga', action.calendar?.id);

		if (action.calendar === undefined || action.calendar === null) {
			console.error('saveCalendar received a null');
			throw new Error('saveCalendar received a null');
		}

		if (!(action.calendar instanceof Calendar)) {
			action.calendar = new Calendar(action.calendar);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.calendar.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.calendar);
			const addResult = yield call(idb.add, 'calendar', action.calendar);
			if (addResult.isErr()) {
				console.error(`⮂ saveCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCalendarSave(action.calendar, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CALENDAR_SAVE_FAILURE, calendar: action.calendar, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CALENDAR_SAVE, calendar: action.calendar, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCalendarSave(action.calendar, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CALENDAR_SAVE_FAILURE, calendar: action.calendar, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CalendarAction} action
 * @param {Calendar[]} action.calendars
 */
export function* saveManyCalendars(action: CalendarAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CALENDAR_SAVE_MANY saga', action.calendars.map(calendar => calendar.id).join(','));

        if (!Array.isArray(action.calendars)) {
            console.error('saveManyCalendars expected an array, received', action.calendars);
            throw new Error('saveManyCalendars expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCalendar), under these queue keys
		const dispatchedAddedOns: number[] = action.calendars.map(calendar => calendar?.addedOn);
		const revertAll = () => action.calendars.map((calendar, i) =>
			put(Actions.revertCalendarSave(calendar, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Calendar[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.calendars.length; i++) {
				const calendar = action.calendars[i];
				calendar.persisted = false;
				ensureOperationId(calendar);
				toSave.push(calendar);
			}

			const addResult = yield call(idb.add, 'calendar', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCalendars: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CALENDAR_SAVE_MANY_FAILURE, calendars: action.calendars, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CALENDAR_SAVE_MANY, calendars: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CALENDAR_SAVE_MANY_FAILURE, calendars: action.calendars, status: 500, err});

			console.error(`Error saving ${action.calendars.length} calendars to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CalendarAction} action
 */
export function* removeCalendar(action: CalendarAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.calendar === undefined || action.calendar === null || !(action.calendar instanceof Calendar)) {
			console.error('removeCalendar expected type Calendar, received', action.calendar);
			reportQueued(false);
			throw new Error('removeCalendar expected type Calendar');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.calendar.status = 'D';
		action.calendar.addedOn = newHRTimestamp();
		action.calendar.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.calendar);
		const addResult = yield call(idb.add, 'calendar', action.calendar);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CALENDAR_REMOVE_FAILURE, calendar: action.calendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CALENDAR_REMOVE, calendar: action.calendar});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDAR_REMOVE_FAILURE, calendar: action.calendar, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Calendar.
	*
	* @param {CalendarAction} action
	*/
export function* restoreCalendar(action: CalendarAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCalendar expected getCurrentUserId() to return a value');
        return;
    }

	if (action.calendar === undefined || action.calendar === null) {
        console.error('restoreCalendar expected a Calendar, received null');
        throw new Error('restoreCalendar expected a Calendar, received null');
    }
    else if (!(action.calendar instanceof Calendar)) action.calendar = new Calendar(action.calendar); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.calendar.status = 'U';
		action.calendar.addedOn = newHRTimestamp();
		action.calendar.addedBy = userId;
		action.calendar.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.calendar);
		const addResult = yield call(idb.add, 'calendar', action.calendar);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCalendar: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CALENDAR_RESTORE_FAILURE, calendar: action.calendar, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CALENDAR_RESTORE, calendar: action.calendar});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDAR_RESTORE_FAILURE, calendar: action.calendar, status: 500, err});

		console.error('Error restoring from database', err);
	}
}